import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFetcher, useNavigate } from 'react-router';
import type { CreateOrUpdateBuildHubMutation } from 'generated/graphql';
import { BUILD_HUB_UPSERT_API_ROUTE } from '~/routes/remix-api.app.build-hub-create';

interface UseUpdateBuildHubOptions {
    onSuccess?: () => void;
    shouldNavigateOnSuccess?: boolean;
}

interface UseUpdateBuildHubReturn {
    updateBuildHub: (formDataObj: FormData) => void;
    isUpdating: boolean;
    error: string | null;
    isSuccess: boolean;
    isIdle: boolean;
}

/**
 * Reusable hook for updating BuildHub records
 *
 * Handles the GraphQL mutation, loading states, error handling, and success callbacks.
 * Can be used in both the creation flow and edit form.
 */
export function useBuildHubUpdate(
    options: UseUpdateBuildHubOptions = {},
): UseUpdateBuildHubReturn {
    const { onSuccess, shouldNavigateOnSuccess = false } = options;
    const navigate = useNavigate();
    const updateFetcher = useFetcher<CreateOrUpdateBuildHubMutation>();

    // Stable ref for onSuccess to prevent effect re-firing on every render
    const onSuccessRef = useRef(onSuccess);

    // Update ref when onSuccess changes
    useEffect(() => {
        onSuccessRef.current = onSuccess;
    }, [onSuccess]);

    // Derived states
    const data = updateFetcher.data?.buildHubUpsert;
    const isUpdating = updateFetcher.state !== 'idle';
    const isIdle = !isUpdating;
    const isSuccess = Boolean(isIdle && data && data.__typename === 'BuildHub');

    /**
     * Submit update request
     */
    const updateBuildHub = useCallback(
        (formDataObj: FormData) => {
            updateFetcher.submit(formDataObj, {
                method: 'post',
                action: BUILD_HUB_UPSERT_API_ROUTE,
            });
        },
        [updateFetcher],
    );

    /**
     * Handle successful update response
     */
    useEffect(() => {
        if (
            updateFetcher.state === 'idle' &&
            data &&
            data?.__typename === 'BuildHub'
        ) {
            if (onSuccessRef.current) {
                onSuccessRef.current();
            }

            // Navigate to project page if requested (creation flow)
            if (shouldNavigateOnSuccess) {
                navigate(`/app/project/${data.URLSegment}`);
            }
        }
    }, [updateFetcher.state, data, navigate, shouldNavigateOnSuccess]);

    /**
     * Extract error message from response
     */
    const error = useMemo(() => {
        if (isIdle && data && data.__typename !== 'BuildHub') {
            return 'Message' in data
                ? data.Message
                : 'Failed to update project';
        }
        return null;
    }, [isIdle, data]);

    return {
        updateBuildHub,
        isUpdating,
        error,
        isSuccess,
        isIdle,
    };
}
